/**
 * Bildirim Ayarları Popup'u
 * Sidebar'daki zil butonuna tıklanınca açılır.
 * Master açık/kapalı (backend), event checkboxları + ses ayarı (localStorage).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Bell, BellOff, Volume2, VolumeX } from 'lucide-react';
import {
  NotifSettings,
  getNotifSettings,
  saveNotifSettings,
  playNotifSound,
  EventKey,
} from '../notifUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  notificationsOn: boolean;
  onToggle: () => void;
  dark: boolean;
  onClose: () => void;
}

// ─── Event listesi ────────────────────────────────────────────────────────────

const EVENT_LABELS: { key: EventKey; emoji: string; label: string }[] = [
  { key: 'order_sent',   emoji: '📤', label: 'Emir Gönderildi'       },
  { key: 'order_filled', emoji: '✅', label: 'Emir Gerçekleşti'      },
  { key: 'tp_filled',    emoji: '🎯', label: 'TP Gerçekleşti'        },
  { key: 'drip_chunk',   emoji: '🔁', label: 'Drip Chunk Tamamlandı' },
  { key: 'kill_switch',  emoji: '🛑', label: 'Kill Switch'           },
  { key: 'other',        emoji: '🔔', label: 'Diğer Bildirimler'     },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function NotificationSettingsPopup({ notificationsOn, onToggle, dark, onClose }: Props) {
  const [settings, setSettings] = useState<NotifSettings>(getNotifSettings);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside → kapat
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Ayar değişince localStorage'a kaydet
  const update = useCallback((patch: Partial<NotifSettings>) => {
    setSettings(prev => {
      const next = { ...prev, ...patch };
      saveNotifSettings(next);
      return next;
    });
  }, []);

  const toggleEvent = (key: EventKey) => {
    const next = { ...settings.events, [key]: !settings.events[key] };
    update({ events: next });
  };

  const bg     = dark ? 'bg-[#061530] border-[#0a2560]' : 'bg-white border-[#C5D3E8]';
  const text   = dark ? 'text-blue-100' : 'text-[#001A4D]';
  const sub    = dark ? 'text-blue-400' : 'text-[#5A6E8C]';
  const divBdr = dark ? 'border-[#0a2560]' : 'border-[#E8EEF7]';
  const rowHov = dark ? 'hover:bg-[#0a2560]/40' : 'hover:bg-[#E8EEF7]/60';
  const chkCls = 'w-3.5 h-3.5 rounded accent-blue-500 cursor-pointer flex-shrink-0';

  return (
    <div
      ref={ref}
      className={`
        absolute bottom-full left-3 mb-2 w-72 rounded-xl border shadow-2xl z-50
        ${bg}
      `}
    >
      {/* Başlık */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${divBdr}`}>
        <span className={`text-sm font-semibold ${text}`}>Bildirim Ayarları</span>
        <button onClick={onClose} className={`${sub} hover:text-white transition-colors`}>
          <X size={14} />
        </button>
      </div>

      {/* Master toggle */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${divBdr}`}>
        <div className="flex items-center gap-2">
          {notificationsOn
            ? <Bell size={14} className="text-blue-400" />
            : <BellOff size={14} className={sub} />
          }
          <span className={`text-xs font-medium ${text}`}>
            {notificationsOn ? 'Bildirimler Açık' : 'Bildirimler Kapalı'}
          </span>
        </div>
        <button
          onClick={onToggle}
          className={`relative w-9 h-5 rounded-full transition-colors ${notificationsOn ? 'bg-blue-600' : 'bg-[#0a2560]'}`}
        >
          <span className={`
            absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform
            ${notificationsOn ? 'translate-x-4' : 'translate-x-0'}
          `} />
        </button>
      </div>

      {/* Event checkboxları */}
      <div className={`px-3 py-2 border-b ${divBdr}`}>
        <p className={`text-[10px] uppercase tracking-widest font-semibold mb-1.5 px-1 ${sub}`}>
          Bildirim Türleri
        </p>
        {EVENT_LABELS.map(({ key, emoji, label }) => (
          <label
            key={key}
            className={`flex items-center gap-2.5 px-1 py-1.5 rounded-lg cursor-pointer ${rowHov} transition-colors`}
          >
            <input
              type="checkbox"
              className={chkCls}
              checked={settings.events[key]}
              onChange={() => toggleEvent(key)}
            />
            <span className="text-sm leading-none">{emoji}</span>
            <span className={`text-xs ${text}`}>{label}</span>
          </label>
        ))}
      </div>

      {/* Ses ayarları */}
      <div className="px-3 py-3 space-y-3">
        {/* Ses aç/kapat */}
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            {settings.soundEnabled
              ? <Volume2 size={14} className="text-blue-400" />
              : <VolumeX size={14} className={sub} />
            }
            <span className={`text-xs font-medium ${text}`}>Bildirim Sesi</span>
          </div>
          <button
            onClick={() => update({ soundEnabled: !settings.soundEnabled })}
            className={`relative w-9 h-5 rounded-full transition-colors ${settings.soundEnabled ? 'bg-blue-600' : 'bg-[#0a2560]'}`}
          >
            <span className={`
              absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform
              ${settings.soundEnabled ? 'translate-x-4' : 'translate-x-0'}
            `} />
          </button>
        </div>

        {/* Ses seviyesi slider */}
        <div className={`px-1 space-y-1 transition-opacity ${settings.soundEnabled ? 'opacity-100' : 'opacity-30 pointer-events-none'}`}>
          <div className="flex items-center justify-between">
            <span className={`text-[10px] ${sub}`}>Ses Seviyesi</span>
            <span className={`text-[10px] font-mono ${sub}`}>{settings.volume}%</span>
          </div>
          <input
            type="range"
            min={0} max={100} step={5}
            value={settings.volume}
            onChange={e => update({ volume: Number(e.target.value) })}
            onMouseUp={() => playNotifSound(settings.volume)}
            className="w-full h-1.5 rounded-full accent-blue-500 cursor-pointer"
          />
          <div className={`flex justify-between text-[9px] ${sub}`}>
            <span>0</span><span>50</span><span>100</span>
          </div>
        </div>
      </div>
    </div>
  );
}
